<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useJobStore } from '@/stores/job'
import { useCustomerStore } from '@/stores/customer'
import { useJobTemplateStore } from '@/stores/jobTemplate'
import SelectCustomer from './base/SelectCustomer.vue'
import SelectCustomerAuto from './base/SelectCustomerAuto.vue'
import { useJobCategoryStore } from '@/stores/jobCategory'
import { useAreaStore } from '@/stores/area'

import JobCategoryForm from './JobCategoryForm.vue'
import AreaForm from './AreaForm.vue'
import SelectAutoComplete from './base/SelectAutoComplete.vue'
import { onBeforeRouteLeave } from 'vue-router'

const jobStore = useJobStore()
const jobTemplateStore = useJobTemplateStore()
const customerStore = useCustomerStore()
const jobCategoryStore = useJobCategoryStore()
const areaStore = useAreaStore()

onMounted(async () => {
  await Promise.all([
    customerStore.getAllCustomers(),
    // jobTemplateStore.getAllJobTemplates(),
    jobCategoryStore.getAllJobCategories(),
    areaStore.getAllAreas()
  ])
})

const job = defineModel()

const showCustomerDialog = ref(false)

const insertNewCustomer = (customerId) => {
  customerId && (job.value.customerId = customerId)
  showCustomerDialog.value = false
}

const selectedTemplate = ref(job.value.name)

const filteredTemplates = ref([])

const changeJobName = (jobName) => {
  if (selectedTemplate.value?.id && typeof selectedTemplate.value === 'object') {
    // console.log('🚀 ~ JobForm.vue:55 ~ changeJobName ~ selectedTemplate:', selectedTemplate.value)
    if (!job.value.description) job.value.description = selectedTemplate.value.description
    if (!job.value.price) job.value.price = selectedTemplate.value.price
    if (!job.value.category) job.value.category = selectedTemplate.value.category
    job.value.jobTemplateId = selectedTemplate.value.id
    job.value.name = selectedTemplate.value.name
  } else {
    job.value.name = jobName
    job.value.jobTemplateId = null
  }
}

watch(selectedTemplate, changeJobName)

const emptyTemplateMessage = computed(() => {
  let message
  if (!job.value.name) message = 'אין עדיין עבודות קודמות להוספה'
  else message = 'אין עבודות קודמות בשם זה'
  return message
})

const searchTemplate = (event) => {
  //   changeJobName(event.query)
  setTimeout(() => {
    if (!event.query.trim().length) {
      filteredTemplates.value = [...jobTemplateStore.jobTemplates]
    } else {
      filteredTemplates.value = jobTemplateStore.jobTemplates.filter((template) =>
        template.name?.toLowerCase().startsWith(event.query.toLowerCase())
      )
    }
  }, 250)
}

const showJobCategoryDialog = ref(false)
const closeJobCategoryDialog = (id) => {
  showJobCategoryDialog.value = false
  if (id) {
    job.value.jobCategoryId = id
  }
}

const showAreaDialog = ref(false)
const closeAreaDialog = (id) => {
  showAreaDialog.value = false
  if (id) {
    job.value.areaId = id
  }
}

// בבחירת לקוח, אם משויך לאזור והאזור עדיין ריק - מלא אוטומטית
watch(
  () => job.value.customerId,
  (newCustomerId) => {
    const customer = customerStore.customers.find((c) => c.id === newCustomerId)
    if (customer && customer.areaId && !job.value.areaId) {
      job.value.areaId = customer.areaId
    }
  }
)

const categoryInputValue = ref('')
const areaInputValue = ref('')

onBeforeRouteLeave((to, from) => {
  if (showJobCategoryDialog.value) {
    showJobCategoryDialog.value = false
    return false // ביטול הניווט
  }

  if (showAreaDialog.value) {
    showAreaDialog.value = false
    return false // ביטול הניווט
  }
})
</script>

<template>
  <div class="grid gap-4">
    <div class="col-span-2 flex flex-col sm:col-span-1">
      <label for="customerId"> לקוח </label>
      <!-- <SelectCustomer label-id="customerId" v-model="job.customerId" /> -->
      <SelectCustomerAuto v-model="job.customerId" />
    </div>

    <div class="col-span-2 flex flex-col sm:col-span-1">
      <label for="area" class="font-semibold">אזור</label>
      <SelectAutoComplete
        v-model="job.areaId"
        :options="areaStore.areas"
        option-label="name"
        option-value="id"
        placeholder="הקלד או בחר אזור"
        fluid
        dropdown
        @change="
          ({ value }) => (value ? (areaInputValue = value) : (areaInputValue = areaInputValue))
        "
        :virtual-scroller-options="{
          itemSize: 38
        }"
        append-to="self"
      >
        <template #footer>
          <Button
            label="הוסף אזור"
            @click="showAreaDialog = true"
            icon="pi pi-plus"
            severity="secondary"
            fluid
          />
        </template>
      </SelectAutoComplete>
      <Dialog v-model:visible="showAreaDialog" header="הוספת אזור" modal>
        <AreaForm :area="{ name: areaInputValue }" @close="closeAreaDialog" />
      </Dialog>
    </div>

    <div class="col-span-2 flex flex-col sm:col-span-1">
      <label for="category"> סוג עבודה </label>
      <SelectAutoComplete
        :options="jobCategoryStore.jobCategories"
        v-model="job.jobCategoryId"
        option-label="name"
        option-value="id"
        placeholder="הקלד או בחר קטגוריה"
        fluid
        dropdown
        force-selection
        input-id="jobCategory"
        @change="
          ({ value }) =>
            value ? (categoryInputValue = value) : (categoryInputValue = categoryInputValue)
        "
        :virtual-scroller-options="{
          itemSize: 38
        }"
        append-to="self"
      >
        <template #footer>
          <Button
            label="הוסף קטגוריה"
            @click="showJobCategoryDialog = true"
            icon="pi pi-plus"
            severity="secondary"
            fluid
          />
        </template>
      </SelectAutoComplete>
      <Dialog v-model:visible="showJobCategoryDialog" header="הוספת קטגוריית עבודה" modal>
        <JobCategoryForm @close="closeJobCategoryDialog" />
      </Dialog>
    </div>

    <div class="col-span-2 flex flex-col">
      <label for="name"> תיאור העבודה </label>
      <Textarea id="name" v-model="job.name" placeholder="הכנס תיאור עבודה" auto-resize />
    </div>

    <div class="col-span-2 flex items-end gap-4">
      <div class="flex flex-1 flex-col">
        <label for="price"> מחיר </label>
        <InputNumber inputId="price" v-model="job.price" fluid suffix=" ₪" :disabled="job.isNonBillable" />
      </div>
      <div class="flex items-center gap-2">
        <Checkbox v-model="job.isNonBillable" input-id="isNonBillable" binary />
        <label for="isNonBillable">עבודה ללא תשלום</label>
      </div>
    </div>
  </div>
</template>
